import React, { useEffect, useRef, useState } from 'react';
import { Carousel } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import type { CarouselRef } from 'antd/es/carousel';
import { getPublishedHomeBanners } from './actions';
import { PublishedHomeBanner } from './types';
import './home-banners-carousel.scss';

type Props = {
    language: string;
    onBannersChange?: (hasBanners: boolean) => void;
};

const getDotClassName = (isActive: boolean) =>
    isActive
        ? 'home-banners-carousel__dot home-banners-carousel__dot--active'
        : 'home-banners-carousel__dot';

const HomeBannersCarousel = ({ language, onBannersChange }: Props) => {
    const carouselRef = useRef<CarouselRef>(null);
    const [banners, setBanners] = useState<PublishedHomeBanner[]>([]);
    const [currentSlide, setCurrentSlide] = useState(0);

    useEffect(() => {
        let isMounted = true;

        getPublishedHomeBanners(language)
            .then(({ data }) => {
                if (!isMounted) {
                    return;
                }

                const content = data.content || [];
                setBanners(content);
                setCurrentSlide(0);
                onBannersChange?.(content.length > 0);
            })
            .catch((error) => {
                console.error(error);
                if (isMounted) {
                    onBannersChange?.(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [language, onBannersChange]);

    if (banners.length === 0) {
        return null;
    }

    return (
        <div className="home-banners-carousel">
            <Carousel
                ref={carouselRef}
                autoplay
                autoplaySpeed={5000}
                dots={false}
                afterChange={setCurrentSlide}
            >
                {banners.map((banner) => (
                    <a
                        key={banner.id}
                        href={banner.linkUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="home-banners-carousel__slide"
                    >
                        {banner.imageSrc && (
                            <img
                                src={banner.imageSrc}
                                alt=""
                                className="home-banners-carousel__image"
                            />
                        )}
                        <span className="home-banners-carousel__content">
                            <span className="home-banners-carousel__title">{banner.title}</span>
                            {banner.description && (
                                <span className="home-banners-carousel__description">
                                    {banner.description}
                                </span>
                            )}
                        </span>
                    </a>
                ))}
            </Carousel>

            {banners.length > 1 && (
                <div className="home-banners-carousel__dots">
                    {banners.map((banner, index) => (
                        <button
                            key={banner.id}
                            type="button"
                            className={getDotClassName(index === currentSlide)}
                            aria-label={`Перейти к баннеру ${index + 1}`}
                            aria-current={index === currentSlide}
                            onClick={() => carouselRef.current?.goTo(index)}
                        />
                    ))}
                </div>
            )}

            {banners.length > 1 && (
                <>
                    <button
                        type="button"
                        className="home-banners-carousel__arrow home-banners-carousel__arrow--prev"
                        onClick={() => carouselRef.current?.prev()}
                        aria-label="Предыдущий баннер"
                    >
                        <LeftOutlined />
                    </button>
                    <button
                        type="button"
                        className="home-banners-carousel__arrow home-banners-carousel__arrow--next"
                        onClick={() => carouselRef.current?.next()}
                        aria-label="Следующий баннер"
                    >
                        <RightOutlined />
                    </button>
                </>
            )}
        </div>
    );
};

export default HomeBannersCarousel;
